import { Button, FlexBox } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { ChangeEvent } from 'react';
import * as React from 'react';
import Dropzone from 'react-dropzone';

import type { PropsT as WaitMusicWithPlayerPropsT } from '../../waitMusic/components/WaitMusicWithPlayer';

import type { FlowCallUploadFileResponseT } from './types';

const MAX_FILE_SIZE = 20097152; //20097152 ~20mb.
const ALLOWED_FILE_TYPES = 'audio/*';

type PropsT = WaitMusicWithPlayerPropsT;

type StateT = {
  dropzoneText: string;
  file: null | File;
  filename: string;
  filenameErrorLabel: string;
  filenameFormGroup: string;
  readyToUpload: boolean;
  uploadBtndDisabled: string;
};

class Upload extends React.Component<PropsT, StateT> {
  dropzone: React.RefObject<Dropzone>;
  filename: React.RefObject<HTMLInputElement>;

  constructor(props: PropsT) {
    super(props);

    this.dropzone = React.createRef();
    this.filename = React.createRef();

    this.state = {
      dropzoneText: 'Drop files here or click to upload.',
      file: null,
      filename: '',
      filenameErrorLabel: '',
      filenameFormGroup: '',
      readyToUpload: false,
      uploadBtndDisabled: '',
    };
  }

  componentWillUnmount() {
    this.setState({
      dropzoneText: 'Drop files here or click to upload.',
      file: null,
      filename: '',
      filenameErrorLabel: '',
      filenameFormGroup: '',
      readyToUpload: false,
      uploadBtndDisabled: '',
    });
  }

  @boundMethod
  bytesToSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    if (bytes == 0) {
      return '0 Byte';
    }

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
  }

  @boundMethod
  onDrop(acceptedFiles: File[], rejectedFiles: File[]): void {
    if (acceptedFiles.length > 0) {
      if (this.state.filename.trim().length === 0) {
        this.setState({ filename: acceptedFiles[0].name });
      }
      this.setState(
        {
          filenameErrorLabel: '',
          filenameFormGroup: '',
          dropzoneText: `${acceptedFiles[0].name} ${this.bytesToSize(
            acceptedFiles[0].size,
          )} dropped!`,
          file: acceptedFiles[0],
          uploadBtndDisabled: 'btn-success',
          readyToUpload: true,
        },
        () => {
          this.onClickUpload();
        },
      );
    } else if (rejectedFiles.length > 0) {
      if (MAX_FILE_SIZE <= rejectedFiles[0].size) {
        this.setState({
          dropzoneText: `Maximum upload file size is ${this.bytesToSize(
            MAX_FILE_SIZE,
          )}. File size on "${rejectedFiles[0].name}" is ${this.bytesToSize(
            rejectedFiles[0].size,
          )}!`,
          file: null,
          readyToUpload: false,
        });
      } else {
        this.setState({
          dropzoneText: `Only audio files are supported`,
          file: null,
          readyToUpload: false,
        });
      }
    }
  }

  @boundMethod
  onClickUpload(): void {
    if (this.state.file && this.state.filename.trim().length) {
      this.setState({
        uploadBtndDisabled: 'disabled',
        dropzoneText: 'Uploading...',
      });

      this.props
        .waitMusicUpload({
          file: this.state.file,
          filename: this.state.filename,
        })
        .then((response: FlowCallUploadFileResponseT) => {
          this.setState({
            dropzoneText: 'Spliting...',
            uploadBtndDisabled: 'disabled',
          });
          this.props
            .waitMusicSplit({
              masterFilePath: response.data.relative_uri,
              name: this.state.filename,
            })
            .then(() => {
              this.setState({
                dropzoneText: 'Upload complete',
                uploadBtndDisabled: '',
              });
              this.props.waitMusicWaitingForSplitingOpen();
            })
            .catch((err: Error) => {
              console.error('waitMusicSplit Exception', err);
              this.setState({
                uploadBtndDisabled: '',
                dropzoneText: 'An error occurred...',
              });
            });
        })
        .catch((err: Error) => {
          console.error('Exception', err);
        });
    } else {
      this.setState({
        filenameErrorLabel: 'Please add a filename',
        filenameFormGroup: 'has-error',
      });
      if (this.filename.current) {
        this.filename.current.focus();
      }
    }
  }

  @boundMethod
  onOpenDropzoneClick(): void {
    if (this.dropzone && this.dropzone.current) {
      this.dropzone.current.open();
    }
  }

  @boundMethod
  changeFilename(event: ChangeEvent<HTMLInputElement>): void {
    this.setState({ filename: event.target.value });
  }

  render() {
    let status = 'Select a file to upload';

    if (this.props.meta.uploadAsyncStatus === 'succeeded') {
      if (this.state.readyToUpload === true) {
        status = 'Upload file';
      }
    } else if (this.props.meta.uploadAsyncStatus === 'loading') {
      status = 'Please wait..';
    }

    if (this.state.dropzoneText === 'Uploading...') {
      status = this.state.dropzoneText;
    }

    return (
      <FlexBox flexDirection="column" gap="300">
        <div className={'form-group ' + this.state.filenameFormGroup}>
          <label className="control-label" htmlFor="filename">
            Title
          </label>
          <input
            id="filename"
            ref={this.filename}
            onChange={this.changeFilename}
            value={this.state.filename}
            className="form-control"
            type="text"
            placeholder="eg. My custom music..."
          />
        </div>

        <Dropzone
          ref={this.dropzone}
          onDrop={this.onDrop}
          accept={ALLOWED_FILE_TYPES}
          multiple={false}
          className="dropzone"
          maxSize={MAX_FILE_SIZE}
        >
          <div>
            <p>{this.state.dropzoneText}</p>
          </div>
        </Dropzone>
        <Button
          type="button"
          disabled={this.state.uploadBtndDisabled === 'disabled'}
          id="upload"
          onClick={
            this.state.readyToUpload
              ? this.onClickUpload
              : this.onOpenDropzoneClick
          }
        >
          {status}
        </Button>
      </FlexBox>
    );
  }
}

export default Upload;
